import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, pluck, switchMap, take, timeout } from 'rxjs/operators';
import { Apollo } from 'apollo-angular';
import { FileEntityQuery, FilesQuery } from '@app/pages/cmms/graphql/file.queries';
import {
	FileDocumentType,
	ICmmsFilesQueryVariables,
	IFileEntity,
	IFilePaginatedData,
	IFilePayload,
	IFilesWithPaginationData,
	ILinkFilesObjectsVariables,
} from '@app/pages/cmms/interfaces/files.interfaces';
import {
	DeleteFile,
	EditFileEntityMutation,
	linkFilesObjectsMutation,
	removeClassificationSuggestion,
	startFileClassificationJob,
	UnlinkFileEntityObjects,
	updateFileInsideEntityMutation,
	updateFiles,
} from '@app/pages/cmms/graphql/files.mutations';
import { FileValidation } from '@shared/ui-components/au-file-loader/helpers/file-validation.class';
import { IconSnackBarService } from '@core/services/icon-snack-bar.service';
import { CmmsPages, ICmmsState } from '@app/pages/cmms/state/cmms.reducer';
import { Store } from '@ngrx/store';
import {
	replacingFileStarts,
	setFileForEditing,
	startLoadingFiles,
	storeFilesQueryVariables,
} from '@app/pages/cmms/components/files/state/files.action';
import { FormGroup } from '@angular/forms';
import { IDroppedFile } from '@shared/ui-components/au-file-loader/au-file-loader.interfaces';
import { fileTypesConfig } from '@shared/ui-components/au-file-loader/au-file-loader.config';
import { AuErrorAdapter } from '@app/shared';
import { fileQueryVariables } from '../components/files/state/files.selectors';
import { Router } from '@angular/router';
import { AuRightSidebarService } from '@core/services/au-right-sidebar.service';
import { ACTIVE_PAGE_CONFIG } from '@app/pages/cmms/configs/active-page.config';
import { IAiSuggestionJob } from '@app/shared/action-center/interfaces';

@Injectable({
	providedIn: 'root',
})
export class FilesService {
	private fileValidation: FileValidation;

	constructor(
		private apollo: Apollo,
		private notify: IconSnackBarService,
		private store$: Store<ICmmsState>,
		private sidebarService: AuRightSidebarService,
		private router: Router
	) {
		this.fileValidation = new FileValidation(fileTypesConfig);
	}

	loadFileEntities(
		variables: ICmmsFilesQueryVariables
	): Observable<IFilesWithPaginationData> {
		// Due to filter and sort service are provided in component,
		// we need to store the query variables in store to use that in effects
		// when we update table after creating new item
		this.store$.dispatch(storeFilesQueryVariables({ variables }));

		return this.apollo
			.query<IFilePaginatedData>({
				query: FilesQuery,
				fetchPolicy: 'network-only',
				variables,
			})
			.pipe(
				pluck('data'),
				map(data => this.getRequestData<IFilesWithPaginationData>(data))
			);
	}

	editFile(
		variables: Partial<IFilePayload>,
		unlink?: { pk: string; incidents?: string; requests?: string; workOrders?: string }[]
	): Observable<IFileEntity> {
		unlink?.map(remove => {
			this.unlinkAttachedElements(remove).subscribe({
				error: error => {
					this.notify.error(AuErrorAdapter.getTextFromGqlError(error));
				},
			});
		});

		return this.apollo
			.mutate<IFileEntity>({
				mutation: EditFileEntityMutation,
				variables,
			})
			.pipe(pluck('data', 'updateFile', 'file')) as Observable<IFileEntity>;
	}

	getUpdatedFiles() {
		if (!this.router.url.includes('files')) {
			return;
		}
		this.store$
			.select(fileQueryVariables)
			.pipe(take(1))
			.subscribe(queryVariables => {
				this.store$.dispatch(startLoadingFiles({ variables: queryVariables }));
			});
	}

	replaceFileInsideEntity(
		file: File,
		fileEntityPk: string,
		name: string
	): Observable<IFileEntity> {
		const errors = this.fileValidation.getFileValidationErrors(file);

		if (!file) {
			return;
		}

		if (Object.keys(errors).length) {
			this.notify.error(Object.values(errors).join(' '));
			return;
		}

		this.store$.dispatch(replacingFileStarts());

		// Federation API doesn't support multipart requests
		return this.apollo
			.use('cmms')
			.mutate<{ createFile: { success: boolean } }>({
				mutation: updateFileInsideEntityMutation,
				variables: {
					file,
					fileEntity: fileEntityPk,
					name,
				},
				context: {
					useMultipart: true,
				},
			})
			.pipe(
				switchMap((value: any) => {
					if (value.data.createFile.success) {
						return this.getFileEntityByPk(fileEntityPk);
					}
				})
			);
	}

	deleteFiles(ids: string[]): Observable<any> {
		return this.apollo
			.mutate<{ success: boolean }>({
				mutation: DeleteFile,
				variables: {
					files: ids,
				},
			})
			.pipe(map((value: any) => value.data.deleteFiles));
	}

	unlinkAttachedElements(variables: {
		pk: string;
		workOrders?: string;
		requests?: string;
		incidents?: string;
	}) {
		return this.apollo
			.mutate<{ success: boolean }>({
				mutation: UnlinkFileEntityObjects,
				fetchPolicy: 'network-only',
				variables,
			})
			.pipe(take(1), pluck('data'));
	}

	// TODO I suppose someone was trying to make this method reusable but now
	//  it's confusing with name that similar to entity type name)
	getRequestData<T>(data, entityName: string = 'files'): T {
		const entities = data[entityName].edges.map(edge => edge.node);
		const paginationName = `${entityName}Pagination`;

		return {
			[entityName]: entities,
			[paginationName]: {
				pageInfo: data[entityName].pageInfo,
				totalCount: data[entityName].totalCount,
			},
		} as T;
	}

	deleteFileFromFormValue(removedIds: string[], form: FormGroup, fieldName = 'files') {
		const files = form.value[fieldName].filter(file => !removedIds.includes(file.pk));

		form.patchValue({ files });
	}

	addOnlyNewFilesToForm(
		filesList: Partial<IDroppedFile>[],
		form: FormGroup,
		fieldName = 'files'
	) {
		const filePrivateKeys = form.value[fieldName].map(f => f.pk);

		filesList.forEach(addedFile => {
			if (addedFile.pk && !filePrivateKeys.includes(addedFile.pk)) {
				const files = form.value[fieldName];

				form.patchValue({
					[fieldName]: files.concat([
						{
							file: {
								name: addedFile.name,
								contentType: addedFile.contentType,
								pk: addedFile.pk,
								url: addedFile.url,
							},
							pk: addedFile.pk,
							name: addedFile.name,
						},
					]),
				});
			}
		});
	}

	linkFilesObjects(variables: ILinkFilesObjectsVariables) {
		return this.apollo
			.mutate<{ success: boolean }>({
				mutation: linkFilesObjectsMutation,
				fetchPolicy: 'network-only',
				variables,
			})
			.pipe(take(1));
	}

	updateFilesType(files: string[], type: FileDocumentType) {
		return this.apollo.mutate<{ success: boolean }>({
			mutation: updateFiles,
			fetchPolicy: 'network-only',
			variables: { files, type },
		});
	}

	getFileEntityByPk(pk: string) {
		return this.apollo
			.query<{ file: IFileEntity }>({
				query: FileEntityQuery,
				variables: {
					pk,
				},
			})
			.pipe(map(res => res.data?.file));
	}

	removeClassificationSuggestion(payload: {
		mappings: {
			fileId: string;
			classifications: string[];
		}[];
	}) {
		return this.apollo
			.mutate<{ removeClassificationSuggestions: boolean }>({
				mutation: removeClassificationSuggestion,
				fetchPolicy: 'network-only',
				variables: payload,
			})
			.pipe(map(res => res.data?.removeClassificationSuggestions));
	}

	startAiClassificationSuggestionJob(filePks: string[]) {
		return this.apollo
			.mutate<{
				startFileClassificationJob: {
					job: IAiSuggestionJob;
				};
			}>({
				mutation: startFileClassificationJob,
				fetchPolicy: 'network-only',
				variables: { filePks },
			})
			.pipe(
				timeout(10000),
				map(res => res.data?.startFileClassificationJob)
			);
	}

	viewFileInWorkspace(file: IFileEntity): void {
		// The one that was before gives too complicated error by using from here
		this.store$.dispatch(setFileForEditing({ fileEdit: file }));

		this.sidebarService.setComponentAndOpen(
			ACTIVE_PAGE_CONFIG[CmmsPages.editFile].createComponent,
			{
				...ACTIVE_PAGE_CONFIG[CmmsPages.editFile].sidebarConfig,
				title: {
					text: 'file_id',
					endfix: file.fileEntityId,
				},
			}
		);
	}
}
